import { useEffect } from 'react';
import { Button } from '@accure/ui';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { CloudUpload } from 'lucide-react';

import { FieldSelect } from '@/components/looker/field-select';
import { FieldText } from '@/components/looker/field-text';
import { Tooltip } from '@/components/ui/tooltip';
import { DocumentType } from '@/modules/documents/domain/entities/document-type';

import type { Filters } from './router/hooks/use-filters';

export type DocumentsFiltersProps = {
  filters: Filters<{
    type?: DocumentType;
    search?: string;
  }>;
  onUploadClick?: () => void;
};

export function DocumentsFilters({
  filters,
  onUploadClick,
}: DocumentsFiltersProps) {
  const searchFieldId = 'filter-search';

  useEffect(() => {
    const searchElement = document.getElementById(searchFieldId); // ref is not used by @looker/components
    if (searchElement) {
      searchElement.focus();
    }
  }, []);

  return (
    <div className="flex flex-row justify-between gap-4">
      <div className="flex flex-row items-center gap-4">
        <FieldText
          id={searchFieldId}
          type="search"
          label="Filter"
          iconBefore={<MagnifyingGlassIcon />}
          value={filters.filters.search ?? ''}
          onChange={(e) =>
            filters.update('search', e.currentTarget.value || undefined)
          }
        />
        <FieldSelect
          label="Document Type"
          options={Object.values(DocumentType).map((type) => ({
            value: type.toString(),
          }))}
          value={filters.filters.type}
          onChange={(value) =>
            filters.update(
              'type',
              Object.entries(DocumentType).find(([_, v]) => v === value)?.[1],
            )
          }
          isClearable={true}
        />
      </div>
      <Tooltip content="Upload Document" position="top" delay>
        <Button onClick={onUploadClick} variant="primaryOutline">
          <CloudUpload />
          Upload a document
        </Button>
      </Tooltip>
    </div>
  );
}
